// Browser side of the installable app (SPEC §11, D35): service worker, install prompt, Windows notifications.
import type { NotificationCategory } from "@/lib/notification-categories";

const PREF_KEY = "desktop-notifications";
// Reminders and alerts from SPEC §8 (time reminder, cards, mentions, due digest, budget, sync issues).
const DESKTOP_CATEGORIES = new Set<NotificationCategory>(["bexio", "projects"]);

type InstallPromptEvent = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: string }> };

let installPrompt: InstallPromptEvent | null = null;
let started = false;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((fn) => fn());

export function subscribeDesktopApp(fn: () => void) {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export function startDesktopApp() {
  if (started || typeof window === "undefined") return;
  started = true;
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.register("/sw.js", { scope: "/", updateViaCache: "none" }).catch(() => undefined);
  }
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    installPrompt = e as InstallPromptEvent;
    emit();
  });
  window.addEventListener("appinstalled", () => {
    installPrompt = null;
    emit();
  });
}

export const canPromptInstall = () => installPrompt !== null;

export async function promptInstall() {
  if (!installPrompt) return false;
  const prompt = installPrompt;
  installPrompt = null;
  await prompt.prompt();
  const { outcome } = await prompt.userChoice;
  emit();
  return outcome === "accepted";
}

export const isInstalledApp = () =>
  typeof window !== "undefined" && window.matchMedia?.("(display-mode: standalone)").matches === true;

export type DesktopNotificationState = "unsupported" | "denied" | "off" | "on";

export function desktopNotificationState(): DesktopNotificationState {
  if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
  if (Notification.permission === "denied") return "denied";
  let pref: string | null = null;
  try {
    pref = localStorage.getItem(PREF_KEY);
  } catch {
    // storage blocked: treat as off
  }
  return Notification.permission === "granted" && pref === "on" ? "on" : "off";
}

export async function setDesktopNotifications(on: boolean): Promise<DesktopNotificationState> {
  if (on && Notification.permission !== "granted") await Notification.requestPermission();
  try {
    localStorage.setItem(PREF_KEY, on ? "on" : "off");
  } catch {
    // storage blocked: the choice lasts until reload
  }
  emit();
  return desktopNotificationState();
}

type InboxItem = {
  id: number;
  read: boolean;
  title: string | null;
  message: string;
  context: string | null;
  link: string | null;
  category: NotificationCategory | null;
};

/** Unread reminder-type items not seen on an earlier poll. Marks every item as seen. */
export function pickNewDesktopItems<T extends InboxItem>(items: T[], seen: Set<number>): T[] {
  const fresh = items.filter((n) => !n.read && !seen.has(n.id) && n.category !== null && DESKTOP_CATEGORIES.has(n.category));
  for (const n of items) seen.add(n.id);
  return fresh;
}

const seen = new Set<number>();
let primed = false;

/** Called with each inbox poll. The first poll is the backlog, so it only primes what has been seen. */
export async function showDesktopNotifications(items: InboxItem[]) {
  const fresh = pickNewDesktopItems(items, seen);
  if (!primed) {
    primed = true;
    return;
  }
  if (fresh.length === 0 || desktopNotificationState() !== "on") return;
  const registration = await navigator.serviceWorker?.getRegistration().catch(() => undefined);
  for (const n of fresh) {
    const title = n.title ?? n.message;
    const body = n.title ? (n.context ?? (n.message !== n.title ? n.message : "")) : "";
    // The tag stops several open windows from showing the same notification twice.
    const options = { body, tag: `hub-${n.id}`, icon: "/icons/icon-192.png", data: { url: n.link ?? "/notifications" } };
    if (registration) {
      await registration.showNotification(title, options);
    } else {
      const shown = new Notification(title, options);
      shown.onclick = () => {
        window.focus();
        window.location.assign(options.data.url);
      };
    }
  }
}
